/**
 * Parse a "go to" search entry into an actionable intent (GEN-3103). Accepts:
 * - a genomic locus: chr1:12,345 or chr1:12,345-12,400 (commas tolerated)
 * - a canonical variant id: chr1-12345-A-G
 * - a SeqOne VCF copy: 2-239176431-T-G (chr prefix optional)
 * - a SeqOne gDNA HGVS copy: chr2(GRCh38):g.239176431T>G
 * - an rsID: rs123
 * - otherwise free text, used to filter the table client-side.
 *
 * Locus / variant-id / rsID entries drive the table selection and the embedded
 * IGV browser; free text narrows the visible rows.
 */
export type GoToResult =
  | { kind: 'locus'; value: string }
  | { kind: 'variantId'; value: string }
  | { kind: 'rsid'; value: string }
  | { kind: 'text'; value: string }

const RSID_RE = /^rs\d+$/i
// chrom-pos-ref-alt, chr prefix optional (our canonical id + SeqOne VCF copy).
const VARIANT_ID_RE = /^(?:chr)?([0-9xymt]+)-(\d+)-([acgtn]+)-([acgtn]+)$/i
// SeqOne gDNA HGVS substitution, e.g. chr2(GRCh38):g.239176431T>G. The assembly
// in parens is optional and ignored (we assume the case's reference matches).
const HGVS_SUB_RE = /^(?:chr)?([0-9xymt]+)(?:\([^)]*\))?:g\.(\d+)([acgtn]+)>([acgtn]+)$/i
// SeqOne gDNA HGVS indel/dup/inv: we cannot derive ref/alt without the reference
// sequence, so land on the locus instead (the region scope + auto-select handles it).
const HGVS_INDEL_RE = /^(?:chr)?([0-9xymt]+)(?:\([^)]*\))?:g\.(\d+)(?:_\d+)?(?:del|ins|dup|inv)/i

/**
 * Normalize a contig to the chr-prefixed, canonical form used across the app
 * (VCF contigs and igv.js hg38 are chr-prefixed). Adds `chr` when missing,
 * uppercases, and folds `MT`/`M` to `chrM` so Ensembl-style entries (e.g. `6`,
 * `MT`) match (GEN-4375).
 */
function normalizeContig(contig: string): string {
  const bare = contig.replace(/^chr/i, '').toUpperCase()
  const canonical = bare === 'MT' ? 'M' : bare
  return `chr${canonical}`
}

/** Build our canonical chrom-pos-REF-ALT id, ensuring a `chr` prefix and uppercase alleles. */
function canonicalVariantId(contig: string, pos: string, ref: string, alt: string): string {
  return `${normalizeContig(contig)}-${pos}-${ref.toUpperCase()}-${alt.toUpperCase()}`
}

export function parseGoTo(input: string): GoToResult {
  const trimmed = input.trim()
  if (RSID_RE.test(trimmed)) return { kind: 'rsid', value: trimmed.toLowerCase() }

  const hgvsSub = HGVS_SUB_RE.exec(trimmed)
  if (hgvsSub) {
    const [, contig, pos, ref, alt] = hgvsSub
    return { kind: 'variantId', value: canonicalVariantId(contig, pos, ref, alt) }
  }

  const hgvsIndel = HGVS_INDEL_RE.exec(trimmed)
  if (hgvsIndel) {
    const [, contig, pos] = hgvsIndel
    return { kind: 'locus', value: `${normalizeContig(contig)}:${pos}` }
  }

  const variant = VARIANT_ID_RE.exec(trimmed)
  if (variant) {
    const [, contig, pos, ref, alt] = variant
    return { kind: 'variantId', value: canonicalVariantId(contig, pos, ref, alt) }
  }

  const locus = /^(?:chr)?([0-9xymt]+):([\d,]+)(?:-([\d,]+))?$/i.exec(trimmed)
  if (locus) {
    const [, contig, start, end] = locus
    const s = start.replace(/,/g, '')
    const range = end ? `-${end.replace(/,/g, '')}` : ''
    return { kind: 'locus', value: `${normalizeContig(contig)}:${s}${range}` }
  }
  return { kind: 'text', value: trimmed }
}

/** Derive an IGV locus (chr1:12345) from a canonical variant id (chr1-12345-A-G). */
export function variantIdToLocus(variantId: string): string | null {
  const parts = variantId.split('-')
  if (parts.length < 2) return null
  const [chrom, pos] = parts
  if (!chrom || !/^\d+$/.test(pos)) return null
  return `${chrom}:${pos}`
}

/**
 * Turn a `locus` entry into an API `region` string the variants endpoint
 * understands (contig:start-end, both bounds required). A single position
 * (chr1:55526878) becomes a `flank`-bp window on each side
 * (chr1:pos-flank..pos+flank, start clamped at 1); an explicit range is
 * returned unchanged regardless of `flank`. Commas are tolerated. Returns null
 * for non-locus input. `flank` defaults to 0 (a 1bp window).
 *
 * Used to scope the variant table to a locus/interval so the variants there
 * are listed (a small flank surfaces neighboring variants for context) and the
 * target can be selected into the detail panel.
 */
export function locusToRegion(locus: string, flank = 0): string | null {
  const m = /^(?:chr)?([0-9xymt]+):(\d+)(?:-(\d+))?$/i.exec(locus.replace(/,/g, ''))
  if (!m) return null
  const [, contig, start, end] = m
  const chrom = normalizeContig(contig)
  if (end !== undefined) return `${chrom}:${start}-${end}`
  const pos = Number(start)
  return `${chrom}:${Math.max(1, pos - flank)}-${pos + flank}`
}
